'use client';

/** Режим матча (лобби / рантайм). */
export type MysteryRoomGameMode = 'coop' | 'survival' | 'stealth' | 'infection';

export type MysteryRoomVec2 = {
  x: number;
  y: number;
};

export type MysteryRoomRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type MysteryRoomVisualOutlineStyle = {
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
};

export type MysteryRoomSegment = {
  start: MysteryRoomVec2;
  end: MysteryRoomVec2;
  layer?: number;
  color?: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  texture?: MysteryRoomSurfaceTexture;
  textures?: MysteryRoomTextureLayer[];
};

export type MysteryRoomAccessLevel = 'public' | 'lab' | 'server' | 'reactor';
export type MysteryRoomAccessCardLevel = 'L2' | 'L3' | 'L4' | 'L5';

export type MysteryRoomNpcRole = 'guard' | 'scientist' | 'technician' | 'medic' | 'operator';
export type MysteryRoomNpcActorKind = 'bot' | 'boss';
export type MysteryRoomNpcActorPreset = 'npc' | 'turret' | 'zombie' | 'custom' | 'boss';
export type MysteryRoomNpcFaction = 'ally' | 'staff' | 'neutral' | 'hostile';
export type MysteryRoomNpcBehaviorProfile =
  | 'civilian'
  | 'guard'
  | 'medic'
  | 'technician'
  | 'operator'
  | 'zombie'
  | 'boss'
  | 'custom';

export type MysteryRoomInventoryItemId = 'stun_baton' | 'flashlight' | 'pistol' | 'medkit' | (string & {});
export type MysteryRoomResourceKind = 'wood' | 'metal' | 'stone';
export type MysteryRoomResourceSourceKind = 'bush' | 'scrap' | 'rock';
export type MysteryRoomPickupItemKind = 'weapon' | 'tool' | 'access_card' | 'resource';

export type MysteryRoomAnchorKind =
  | 'checkpoint'
  | 'workstation'
  | 'experiment'
  | 'medical_station'
  | 'maintenance'
  | 'standby'
  | 'evacuation'
  | 'restroom'
  | 'break_room'
  | 'armory'
  | 'patrol_point'
  | 'medic_work'
  | 'engineer_work'
  | 'guard_work'
  | 'operator_work'
  | 'breakdown';

export type MysteryRoomZoneDefinition = {
  id: string;
  title: string;
  accessLevel: MysteryRoomAccessLevel;
  floor: MysteryRoomRect[];
  color: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  floorTexture?: MysteryRoomSurfaceTexture;
  textures?: MysteryRoomTextureLayer[];
  labelPoint: MysteryRoomVec2;
};

export type MysteryRoomDoorOrientation = 'horizontal' | 'vertical';
export type MysteryRoomDoorAccessMode = 'public' | 'npc_only' | 'card_required' | 'all_cards_required';

export type MysteryRoomDoorDefinition = {
  id: string;
  label?: string;
  fromZoneId: string;
  toZoneId: string;
  requiredAccess: MysteryRoomAccessLevel;
  accessMode?: MysteryRoomDoorAccessMode;
  accessCardObjectIds?: string[];
  panel: MysteryRoomRect;
  trigger: MysteryRoomRect;
  orientation: MysteryRoomDoorOrientation;
  layer?: number;
  color?: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  textures?: MysteryRoomTextureLayer[];
};

export type MysteryRoomOccluderDefinition = {
  id: string;
  rect: MysteryRoomRect;
  blocksMovement: boolean;
  blocksSight: boolean;
};

export type MysteryRoomInteractableKind = 'access_grant' | 'hide_zone' | 'inspect' | 'pickup' | 'light_switch';
export type MysteryRoomWorldLabelVisibility = 'hidden' | 'near_interactive' | 'always';

export type MysteryRoomInteractableDefinition = {
  id: string;
  kind: MysteryRoomInteractableKind;
  zoneId: string;
  anchor: MysteryRoomVec2;
  interactionRadius: number;
  label: string;
  worldLabelVisibility?: MysteryRoomWorldLabelVisibility;
  layer?: number;
  color?: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  textures?: MysteryRoomTextureLayer[];
  requiredAccess?: MysteryRoomAccessLevel;
  grantsAccess?: MysteryRoomAccessLevel;
  itemId?: MysteryRoomInventoryItemId;
  resourceKind?: MysteryRoomResourceKind;
  resourceAmount?: number;
  resourceSourceKind?: MysteryRoomResourceSourceKind;
  inventoryIcon?: string;
  inventoryIconAssetId?: string;
  inventoryIconAssetUrl?: string;
  inventoryIconAssetName?: string;
  inventoryIconAssetMimeType?: string;
  lightReaction?: 'toggle' | 'on' | 'off';
  defaultEnabled?: boolean;
  linkedObjectIds?: string[];
  linkedLightIds?: string[];
  linkedDoorIds?: string[];
  objectiveTag?: string;
  onActivateActions?: MysteryRoomTriggerAction[];
  onPickupActions?: MysteryRoomTriggerAction[];
};

export type MysteryRoomLightDefinition = {
  id: string;
  label?: string;
  zoneId: string;
  position: MysteryRoomVec2;
  radius: number;
  color: number;
  colorTemperatureK?: number;
  intensity: number;
  enabled?: boolean;
  layer?: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  textures?: MysteryRoomTextureLayer[];
  flicker?: boolean;
  flickerMode?: 'steady' | 'flicker' | 'event_flicker' | 'random_bulb';
  flickerIntensity?: number;
  flickerMinIntervalMs?: number;
  flickerMaxIntervalMs?: number;
  switchId?: string;
  switchIds?: string[];
};

export type MysteryRoomHazardDefinition = {
  id: string;
  kind: 'smoke' | 'sparks';
  zoneId: string;
  position: MysteryRoomVec2;
  radius: number;
  intensity: number;
  textures?: MysteryRoomTextureLayer[];
};

export type MysteryRoomPropKind =
  | 'airlock_console'
  | 'locker'
  | 'crate_stack'
  | 'lab_bench'
  | 'microscope'
  | 'sample_tank'
  | 'wall_terminal'
  | 'server_rack'
  | 'server_console'
  | 'medical_bed'
  | 'surgical_lamp'
  | 'med_monitor'
  | 'reactor_core'
  | 'pipe_run'
  | 'turbine'
  | 'floor_grate'
  | 'warning_stripe';

export type MysteryRoomPropDefinition = {
  id: string;
  kind: MysteryRoomPropKind;
  zoneId: string;
  rect: MysteryRoomRect;
  layer?: number;
  color?: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  accentColor?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  rotation?: number;
  label?: string;
  textures?: MysteryRoomTextureLayer[];
};

export type MysteryRoomCustomObjectKind =
  | 'primitive'
  | 'image'
  | 'table'
  | 'wall'
  | 'door'
  | 'equipment'
  | 'badge'
  | 'weapon'
  | 'resource'
  | 'decor';

export type MysteryRoomAssetFit = 'contain' | 'cover' | 'stretch' | 'repeat';

export type MysteryRoomTextureEffects = {
  brightness?: number;
  contrast?: number;
  reflection?: number;
  shading?: number;
  vignette?: number;
  grain?: number;
};

export type MysteryRoomSurfaceTexture = {
  builtinTextureId?: string;
  assetId?: string;
  assetUrl?: string;
  assetName?: string;
  assetMimeType?: string;
  fit?: MysteryRoomAssetFit;
  opacity?: number;
  offsetX?: number;
  offsetY?: number;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  repeatX?: number;
  repeatY?: number;
  tileSize?: number;
  effects?: MysteryRoomTextureEffects;
};

export type MysteryRoomTextureLayer = MysteryRoomSurfaceTexture & {
  id: string;
  label?: string;
  visible?: boolean;
};

export type MysteryRoomAssetTransform = {
  offsetX?: number;
  offsetY?: number;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  clipToBounds?: boolean;
};

export type MysteryRoomCollisionShape =
  | { kind: 'none' }
  | { kind: 'rect'; rect: MysteryRoomRect; blocksMovement: boolean; blocksSight: boolean }
  | { kind: 'circle'; center: MysteryRoomVec2; radius: number; blocksMovement: boolean; blocksSight: boolean }
  | { kind: 'polygon'; points: MysteryRoomVec2[]; blocksMovement: boolean; blocksSight: boolean };

export type MysteryRoomCustomObjectShape =
  | { kind: 'rect' }
  | { kind: 'polygon'; points: MysteryRoomVec2[] };

export type MysteryRoomCustomObjectDefinition = {
  id: string;
  kind: MysteryRoomCustomObjectKind;
  zoneId: string;
  rect: MysteryRoomRect;
  rotation: number;
  color: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  shape?: MysteryRoomCustomObjectShape;
  layer?: number;
  accentColor?: number;
  label: string;
  worldLabelVisibility?: MysteryRoomWorldLabelVisibility;
  assetId?: string;
  assetUrl?: string;
  assetName?: string;
  assetMimeType?: string;
  assetFit?: MysteryRoomAssetFit;
  assetTransform?: MysteryRoomAssetTransform;
  assetAlphaCollisionEnabled?: boolean;
  visualSkinId?: string;
  visualSkinName?: string;
  textures?: MysteryRoomTextureLayer[];
  collision: MysteryRoomCollisionShape;
  collisionEnabled?: boolean;
  physicsEnabled?: boolean;
  weightKg?: number;
  itemId?: MysteryRoomInventoryItemId;
  pickupKind?: MysteryRoomPickupItemKind;
  resourceKind?: MysteryRoomResourceKind;
  resourceAmount?: number;
  resourceSourceKind?: MysteryRoomResourceSourceKind;
  accessCardLevel?: MysteryRoomAccessCardLevel;
  grantsAccess?: MysteryRoomAccessLevel;
  inventoryIcon?: string;
  inventoryIconAssetId?: string;
  inventoryIconAssetUrl?: string;
  inventoryIconAssetName?: string;
  inventoryIconAssetMimeType?: string;
  blocksSight: boolean;
  visibleThroughWalls?: boolean;
  interactive: boolean;
  tags: string[];
  linkedObjectIds?: string[];
  linkedLightIds?: string[];
  linkedDoorIds?: string[];
  objectiveTag?: string;
  onActivateActions?: MysteryRoomTriggerAction[];
  onPickupActions?: MysteryRoomTriggerAction[];
};

export type MysteryRoomTriggerEvent = 'onEnter' | 'onStep';

export type MysteryRoomTriggerAction =
  | { kind: 'toggle_light'; lightIds: string[] }
  | { kind: 'set_light'; lightIds: string[]; enabled: boolean }
  | { kind: 'set_light_flicker'; lightIds: string[]; enabled: boolean; durationMs?: number }
  | { kind: 'play_sound'; soundSourceIds: string[] }
  | { kind: 'spawn_smoke'; hazardId?: string; position?: MysteryRoomVec2; radius?: number }
  | { kind: 'explosion'; position?: MysteryRoomVec2; radius?: number }
  | { kind: 'open_door'; doorIds: string[] }
  | { kind: 'close_door'; doorIds: string[] }
  | { kind: 'enable_equipment'; objectIds: string[]; enabled: boolean }
  | { kind: 'set_alarm'; enabled: boolean };

export type MysteryRoomTriggerDefinition = {
  id: string;
  label: string;
  zoneId: string;
  rect: MysteryRoomRect;
  layer?: number;
  color?: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  textures?: MysteryRoomTextureLayer[];
  event: MysteryRoomTriggerEvent;
  repeatable: boolean;
  cooldownMs: number;
  actions: MysteryRoomTriggerAction[];
};

export type MysteryRoomSoundDefinition = {
  id: string;
  label: string;
  kind: 'footstep' | 'fan' | 'impact' | 'alarm' | 'ambient' | 'custom';
  assetId?: string;
  assetName?: string;
  assetMimeType?: string;
  url?: string;
};

export type MysteryRoomSoundSourceDefinition = {
  id: string;
  label: string;
  zoneId: string;
  position: MysteryRoomVec2;
  soundId: string;
  radius: number;
  volume: number;
  loop: boolean;
  /** Одинаковая громкость по всей карте (без затухания от точки источника). */
  uniformMapVolume?: boolean;
  /** Внутри радиуса зоны — постоянная громкость без «нарастания» к центру. */
  uniformVolumeInRadius?: boolean;
  layer?: number;
  triggerIds?: string[];
};

export type MysteryRoomPatrolNode = {
  x: number;
  y: number;
  zoneId: string;
  pauseMs: number;
};

export type MysteryRoomPatrolRouteDefinition = {
  id: string;
  nodes: MysteryRoomPatrolNode[];
};

export type MysteryRoomTaskAreaShape = 'circle' | 'square';

export type MysteryRoomWorkAnchorDefinition = {
  id: string;
  zoneId: string;
  kind: MysteryRoomAnchorKind;
  position: MysteryRoomVec2;
  taskAreaShape?: MysteryRoomTaskAreaShape;
  taskAreaRadius?: number;
  allowedRoles: MysteryRoomNpcRole[];
  description?: string;
};

export type MysteryRoomNavNodeKind = 'hub' | 'door' | 'work' | 'incident' | 'evac';

export type MysteryRoomNavNodeDefinition = {
  id: string;
  zoneId: string;
  position: MysteryRoomVec2;
  kind: MysteryRoomNavNodeKind;
};

export type MysteryRoomNavEdgeDefinition = {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  distanceCost: number;
  doorId?: string;
  requiredAccess?: MysteryRoomAccessLevel;
};

export type MysteryRoomIncidentAnchorKind =
  | 'alarm'
  | 'fire'
  | 'door_failure'
  | 'reactor_fault'
  | 'server_fault'
  | 'injured_person';

export type MysteryRoomIncidentAnchorDefinition = {
  id: string;
  kind: MysteryRoomIncidentAnchorKind;
  zoneId: string;
  position: MysteryRoomVec2;
};

export type MysteryRoomSpawnSlotDefinition = {
  x: number;
  y: number;
  room?: string;
  zoneId?: string;
  label?: string;
  actor?: 'player' | 'bot';
  side?: 'A' | 'B';
};

export type MysteryRoomNpcDefinition = {
  id: string;
  name: string;
  role: MysteryRoomNpcRole;
  actorKind?: MysteryRoomNpcActorKind;
  actorPreset?: MysteryRoomNpcActorPreset;
  faction?: MysteryRoomNpcFaction;
  behaviorProfile?: MysteryRoomNpcBehaviorProfile;
  spawn: MysteryRoomVec2;
  routeId?: string;
  homeZoneId: string;
  workAnchorIds: string[];
  evacuationNodeId: string;
  dutyActivation?: 'auto' | 'manual' | 'trigger' | 'door_open' | 'world_alarm';
  dutyTriggerIds?: string[];
  dutyDoorIds?: string[];
  accessLevels: MysteryRoomAccessLevel[];
  color: number;
  radius?: number;
  health?: number;
  maxHealth?: number;
  speed?: number;
  visionRange?: number;
  visionHalfAngleDeg?: number;
  scale?: number;
  invisible?: boolean;
};

export type MysteryRoomMapDefinition = {
  id: string;
  title: string;
  /**
   * Если задан непустой массив — карта предлагается только в этих режимах лобби.
   * Пусто / отсутствует — совместима со всеми режимами (поведение по умолчанию).
   */
  playableGameModes?: MysteryRoomGameMode[] | null;
  width: number;
  height: number;
  spawn: MysteryRoomVec2;
  spawnZoneId: string;
  spawnSlots: MysteryRoomSpawnSlotDefinition[];
  visibilityRadius: number;
  rooms: MysteryRoomZoneDefinition[];
  doors: MysteryRoomDoorDefinition[];
  walls: MysteryRoomSegment[];
  occluders: MysteryRoomOccluderDefinition[];
  interactables: MysteryRoomInteractableDefinition[];
  workAnchors: MysteryRoomWorkAnchorDefinition[];
  navNodes: MysteryRoomNavNodeDefinition[];
  navEdges: MysteryRoomNavEdgeDefinition[];
  incidentAnchors: MysteryRoomIncidentAnchorDefinition[];
  patrolRoutes: MysteryRoomPatrolRouteDefinition[];
  npcs: MysteryRoomNpcDefinition[];
  lights: MysteryRoomLightDefinition[];
  hazards: MysteryRoomHazardDefinition[];
  props: MysteryRoomPropDefinition[];
  customObjects: MysteryRoomCustomObjectDefinition[];
  triggers: MysteryRoomTriggerDefinition[];
  soundDefinitions: MysteryRoomSoundDefinition[];
  soundSources: MysteryRoomSoundSourceDefinition[];
};

export function rectContainsPoint(rect: MysteryRoomRect, point: MysteryRoomVec2) {
  return point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;
}

export function rectCenter(rect: MysteryRoomRect): MysteryRoomVec2 {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

export function rectToSegments(rect: MysteryRoomRect): MysteryRoomSegment[] {
  const topLeft = { x: rect.x, y: rect.y };
  const topRight = { x: rect.x + rect.width, y: rect.y };
  const bottomLeft = { x: rect.x, y: rect.y + rect.height };
  const bottomRight = { x: rect.x + rect.width, y: rect.y + rect.height };

  return [
    { start: topLeft, end: topRight },
    { start: topRight, end: bottomRight },
    { start: bottomRight, end: bottomLeft },
    { start: bottomLeft, end: topLeft },
  ];
}

export function polygonToSegments(points: MysteryRoomVec2[]): MysteryRoomSegment[] {
  if (points.length < 2) {
    return [];
  }
  return points.map((point, index) => ({
    start: point,
    end: points[(index + 1) % points.length] ?? point,
  }));
}
